import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { HomepageResponse } from '../types';
import { MuseumHero } from '../components/museum/MuseumHero';
import { ErrorBoundary } from '../components/ui';
import { RecentWorksStrip } from '../components/home/RecentWorksStrip';
import { ArtistSearchEntry, ArtistsStrip } from '../components/home/ArtistsStrip';
import { ExhibitionsStrip } from '../components/home/ExhibitionsStrip';

const HomeContainer = styled.div`
  background: ${({ theme }) => theme.colors.semantic.background};
  min-height: 100vh;
`;

const EmptyHero = styled.section`
  background: ${({ theme }) => theme.colors.semantic.background};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  padding: 8rem 0;
  text-align: center;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    max-width: 1200px;
    margin: 0 auto;
    background: radial-gradient(circle at center, rgba(245, 241, 232, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['5xl']};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: inherit;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.semantic.textSecondary};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: 30px;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.normal};
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  cursor: pointer;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    background-color: ${({ theme }) => theme.colors.semantic.surfaceRaised};
    border-color: ${({ theme }) => theme.colors.semantic.focus};
  }
`;

export function HomePage() {
  const homepageQuery = useQuery<HomepageResponse>({
    queryKey: ['homepage-data'],
    queryFn: () => apiClient.getHomepageData(),
    retry: 1,
    staleTime: 0,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
  });

  const { data: homepageResponse, isLoading, isError, error, refetch } = homepageQuery;
  const homepageDataReady = homepageQuery.isFetchedAfterMount && homepageQuery.isSuccess && !homepageQuery.isError && !homepageQuery.isRefetching && homepageQuery.fetchStatus !== 'paused';
  const homepageData = homepageDataReady ? homepageResponse?.data : undefined;
  const heroData = homepageData?.hero;
  const gridExhibitions = homepageData?.gridExhibitions || [];
  const heroStatus = (!homepageDataReady && (homepageQuery.isFetching || homepageQuery.fetchStatus === 'paused')) || isLoading
    ? 'loading'
    : isError
      ? 'error'
      : heroData?.exhibition
        ? (heroData.artworks.length > 0 ? 'ready' : 'empty')
        : 'empty';
  const collectionSection = homepageData?.collectionSection || (isError
    ? { status: 'unavailable' as const, message: 'Collections are temporarily unavailable.' }
    : { status: 'available' as const });
  const errorMessage = error instanceof Error
    ? error.message
    : 'Homepage data is temporarily unavailable. Please try again.';

  return (
    <ErrorBoundary>
      <HomeContainer>
        {heroStatus !== 'ready' ? (
          <MuseumHero
            artworks={heroData?.artworks || []}
            exhibitionTitle={heroData?.exhibition?.title || 'Home Gallery'}
            status={heroStatus}
            errorMessage={errorMessage}
            onRetry={() => { void refetch(); }}
            autoRotateInterval={8000}
          />
        ) : (
          <MuseumHero
            artworks={heroData?.artworks || []}
            exhibitionTitle={heroData?.exhibition?.title || 'Home Gallery'}
            status="ready"
            autoRotateInterval={8000}
          />
        )}

        {heroStatus === 'empty' && !heroData?.exhibition && (
          <EmptyHero>
            <HeroContent>
              <HeroTitle>Joanne Art Class</HeroTitle>
              <HeroSubtitle>
                A sophisticated platform for art centers to showcase student creativity and curate exceptional exhibitions
              </HeroSubtitle>
              <CTAButton to="/gallery">
                Explore Gallery
              </CTAButton>
            </HeroContent>
          </EmptyHero>
        )}

        <ArtistSearchEntry />
        <RecentWorksStrip />
        <ArtistsStrip />
        <ExhibitionsStrip
          exhibitions={gridExhibitions}
          isLoading={isLoading || (!homepageDataReady && (homepageQuery.isFetching || homepageQuery.fetchStatus === 'paused'))}
          availability={collectionSection}
          isError={isError}
          errorMessage={collectionSection.message}
          onRetry={() => { void refetch(); }}
        />
      </HomeContainer>
    </ErrorBoundary>
  );
}
